import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Settings,
  LogIn,
  Eye,
  EyeOff,
  BarChart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import { BACKEND_URL } from "./Home";

const AdminPanel = () => {
  const [token, setToken] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [activeTab, setActiveTab] = useState("speakers");

  // Data states
  const [speakers, setSpeakers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [mediaPartners, setMediaPartners] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    register_button_url: "",
    program_button_url: "",
    speaker_register_url: "",
    participant_register_url: "",
  });

  // Form states
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});

  const loadData = React.useCallback(
    async (authToken = token) => {
      try {
        const headers = { Authorization: `Bearer ${authToken}` };

        const [
          speakersRes,
          partnersRes,
          mediaPartnersRes,
          scheduleRes,
          settingsRes,
        ] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/speakers`, { headers }),
          fetch(`${BACKEND_URL}/api/admin/partners`, { headers }),
          fetch(`${BACKEND_URL}/api/admin/media-partners`, { headers }),
          fetch(`${BACKEND_URL}/api/admin/schedule`, { headers }),
          fetch(`${BACKEND_URL}/api/admin/settings`, { headers }),
        ]);

        if (speakersRes.ok) setSpeakers(await speakersRes.json());
        if (partnersRes.ok) setPartners(await partnersRes.json());
        if (mediaPartnersRes.ok)
          setMediaPartners(await mediaPartnersRes.json());
        if (scheduleRes.ok) setSchedule(await scheduleRes.json());
        if (settingsRes.ok) setSiteSettings(await settingsRes.json());
      } catch (error) {
        toast.error("Помилка завантаження даних");
      }
    },
    [token]
  );

  const verifyToken = React.useCallback(
    async (tokenToVerify) => {
      setIsLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/speakers`, {
          headers: { Authorization: `Bearer ${tokenToVerify}` },
        });

        if (response.ok) {
          setToken(tokenToVerify);
          setIsAuthenticated(true);
          localStorage.setItem("admin_token", tokenToVerify);
          // Clear token from URL for security
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          toast.success("Авторизація успішна!");
          loadData(tokenToVerify);
        } else {
          toast.error("Невірний токен");
        }
      } catch (error) {
        console.error("Auth error:", error);
        localStorage.removeItem("admin_token");
        setIsAuthenticated(false);
        toast.error("Помилка підключення до сервера");
      }
      setIsLoading(false);
    },
    [loadData]
  );

  // Check URL for token on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");

    if (urlToken) {
      verifyToken(urlToken);
    } else {
      const savedToken = localStorage.getItem("admin_token");
      if (savedToken) {
        verifyToken(savedToken);
      } else {
        setIsLoading(false);
      }
    }
  }, [verifyToken]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (tokenInput.trim()) {
      verifyToken(tokenInput.trim());
    } else {
      toast.error("Введіть токен");
    }
  };

  const handleLogout = () => {
    setToken("");
    setIsAuthenticated(false);
    localStorage.removeItem("admin_token");
    toast.info("Вихід виконано");
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(siteSettings),
      });
      if (response.ok) {
        toast.success(
          "✅ Налаштування збережено! Оновіть головну сторінку, щоб побачити зміни.",
          {
            duration: 5000,
            action: {
              label: "Відкрити сайт",
              onClick: () => window.open("/", "_blank"),
            },
          }
        );
      } else {
        toast.error("Помилка збереження налаштувань");
      }
    } catch (error) {
      toast.error("Помилка збереження налаштувань");
    }
  };

  const handleCreate = async () => {
    try {
      let endpoint = "";

      if (activeTab === "speakers") {
        endpoint = `${BACKEND_URL}/api/admin/speakers`;
      } else if (activeTab === "partners") {
        endpoint = `${BACKEND_URL}/api/admin/partners`;
      } else if (activeTab === "mediaPartners") {
        endpoint = `${BACKEND_URL}/api/admin/media-partners`;
      } else if (activeTab === "schedule") {
        endpoint = `${BACKEND_URL}/api/admin/schedule`;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Створено успішно");
        setShowForm(false);
        setFormData({});
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Помилка створення");
      }
    } catch (error) {
      toast.error("Помилка створення");
    }
  };

  const handleUpdate = async () => {
    try {
      let endpoint = "";

      if (activeTab === "speakers") {
        endpoint = `${BACKEND_URL}/api/admin/speakers/${editingItem.id}`;
      } else if (activeTab === "partners") {
        endpoint = `${BACKEND_URL}/api/admin/partners/${editingItem.id}`;
      } else if (activeTab === "mediaPartners") {
        endpoint = `${BACKEND_URL}/api/admin/media-partners/${editingItem.id}`;
      } else if (activeTab === "schedule") {
        endpoint = `${BACKEND_URL}/api/admin/schedule/${editingItem.id}`;
      }

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Оновлено успішно");
        setShowForm(false);
        setEditingItem(null);
        setFormData({});
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Помилка оновлення");
      }
    } catch (error) {
      toast.error("Помилка оновлення");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити?")) return;

    try {
      let endpoint = "";

      if (activeTab === "speakers") {
        endpoint = `${BACKEND_URL}/api/admin/speakers/${id}`;
      } else if (activeTab === "partners") {
        endpoint = `${BACKEND_URL}/api/admin/partners/${id}`;
      } else if (activeTab === "mediaPartners") {
        endpoint = `${BACKEND_URL}/api/admin/media-partners/${id}`;
      } else if (activeTab === "schedule") {
        endpoint = `${BACKEND_URL}/api/admin/schedule/${id}`;
      }

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok || response.status === 204) {
        toast.success("Видалено успішно");
        loadData();
      } else {
        toast.error("Помилка видалення");
      }
    } catch (error) {
      toast.error("Помилка видалення");
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowForm(true);
  };

  const startCreate = () => {
    setEditingItem(null);
    setFormData(getEmptyForm());
    setShowForm(true);
  };

  const getEmptyForm = () => {
    if (activeTab === "speakers") {
      return {
        name: "",
        title: "",
        title_en: "",
        company: "",
        company_en: "",
        photo: "",
        bio: "",
        bio_en: "",
        social_links: { twitter: "", instagram: "", website: "", linkedin: "" },
        order: 0,
      };
    } else if (activeTab === "partners") {
      return { name: "", logo: "", tier: "none", website_url: "", order: 0 };
    } else if (activeTab === "mediaPartners") {
      return { name: "", logo: "", website_url: "", order: 0 };
    } else if (activeTab === "schedule") {
      return {
        time: "",
        title: "",
        title_en: "",
        speaker: "",
        type: "session",
        description: "",
        description_en: "",
        order: 0,
      };
    }
    return {};
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "3px solid #00ffff",
              borderTopColor: "transparent",
              borderRadius: "50%",
              margin: "0 auto 20px",
              animation: "spin 1s linear infinite",
            }}
          />
          <p>Завантаження...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: "400px",
            background: "#1a1a1a",
            border: "1px solid #333",
          }}
        >
          <CardHeader>
            <CardTitle
              style={{
                color: "#00ffff",
                textAlign: "center",
                fontSize: "24px",
              }}
            >
              🔐 Вхід в адмін-панель
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "#ccc",
                    fontSize: "14px",
                  }}
                >
                  Токен доступу
                </label>
                <div style={{ position: "relative" }}>
                  <Input
                    type={showToken ? "text" : "password"}
                    placeholder="Введіть ваш токен"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                      paddingRight: "40px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#666",
                      cursor: "pointer",
                    }}
                  >
                    {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                style={{
                  width: "100%",
                  background: "#00ffff",
                  color: "#000",
                  fontWeight: "600",
                }}
              >
                <LogIn size={18} style={{ marginRight: "8px" }} />
                Увійти
              </Button>
            </form>
            <p
              style={{
                marginTop: "20px",
                color: "#666",
                fontSize: "12px",
                textAlign: "center",
              }}
            >
              Або використовуйте посилання з токеном: /admin?token=ваш_токен
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin panel
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#000",
        padding: "20px",
        color: "#fff",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <h1 style={{ fontSize: "28px", color: "#00ffff", margin: 0 }}>
            ⚙️ Адмін-панель KICRF
          </h1>
          <button
            onClick={handleLogout}
            style={{
              padding: "10px 20px",
              background: "#d32f2f",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Вийти
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "30px",
            borderBottom: "2px solid #333",
            paddingBottom: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => {
              setActiveTab("speakers");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "speakers" ? "#00ffff" : "#222",
              color: activeTab === "speakers" ? "#000" : "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Спікери ({speakers.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("partners");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "partners" ? "#00ffff" : "#222",
              color: activeTab === "partners" ? "#000" : "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Партнери ({partners.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("mediaPartners");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "mediaPartners" ? "#00ffff" : "#222",
              color: activeTab === "mediaPartners" ? "#000" : "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Медіа-партнери ({mediaPartners.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("schedule");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "schedule" ? "#00ffff" : "#222",
              color: activeTab === "schedule" ? "#000" : "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Програма ({schedule.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("analytics");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "analytics" ? "#00ffff" : "#222",
              color: activeTab === "analytics" ? "#000" : "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            <BarChart
              size={16}
              style={{
                marginRight: "6px",
                display: "inline",
                verticalAlign: "middle",
              }}
            />
            Статистика
          </button>
          <button
            onClick={() => {
              setActiveTab("settings");
              setShowForm(false);
            }}
            style={{
              padding: "10px 20px",
              background: activeTab === "settings" ? "#ff6b6b" : "#222",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            <Settings
              size={16}
              style={{
                marginRight: "6px",
                display: "inline",
                verticalAlign: "middle",
              }}
            />
            Налаштування
          </button>
        </div>

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <Card
            style={{
              marginBottom: "30px",
              background: "#1a1a1a",
              border: "1px solid #333",
            }}
          >
            <CardHeader>
              <CardTitle style={{ color: "#fff" }}>
                Налаштування посилань
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: "grid", gap: "20px" }}>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#00ffff",
                      fontWeight: "600",
                    }}
                  >
                    Кнопка "Зареєструватися" (Hero)
                  </label>
                  <Input
                    placeholder="URL для кнопки реєстрації"
                    value={siteSettings.register_button_url || ""}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        register_button_url: e.target.value,
                      })
                    }
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#00ffff",
                      fontWeight: "600",
                    }}
                  >
                    Кнопка "Повна програма форуму" (Hero)
                  </label>
                  <Input
                    placeholder="URL для кнопки програми"
                    value={siteSettings.program_button_url || ""}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        program_button_url: e.target.value,
                      })
                    }
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#ff6b6b",
                      fontWeight: "600",
                    }}
                  >
                    Кнопка "Зареєструватися спікером"
                  </label>
                  <Input
                    placeholder="URL для реєстрації спікера"
                    value={siteSettings.speaker_register_url || ""}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        speaker_register_url: e.target.value,
                      })
                    }
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "#ff6b6b",
                      fontWeight: "600",
                    }}
                  >
                    Кнопка "Зареєструватися учасником"
                  </label>
                  <Input
                    placeholder="URL для реєстрації учасника"
                    value={siteSettings.participant_register_url || ""}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        participant_register_url: e.target.value,
                      })
                    }
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                    }}
                  />
                </div>

                {/* Statistics Section */}
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1px solid #333",
                  }}
                >
                  <h3
                    style={{
                      color: "#00ffff",
                      marginBottom: "15px",
                      fontSize: "18px",
                    }}
                  >
                    Статистика
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#00ffff",
                          fontWeight: "600",
                        }}
                      >
                        Експерти (Experts)
                      </label>
                      <Input
                        placeholder="Наприклад: 50+"
                        value={siteSettings.stats_experts || ""}
                        onChange={(e) =>
                          setSiteSettings({
                            ...siteSettings,
                            stats_experts: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#00ffff",
                          fontWeight: "600",
                        }}
                      >
                        Учасники (Attendees)
                      </label>
                      <Input
                        placeholder="Наприклад: 1000+"
                        value={siteSettings.stats_attendees || ""}
                        onChange={(e) =>
                          setSiteSettings({
                            ...siteSettings,
                            stats_attendees: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#00ffff",
                          fontWeight: "600",
                        }}
                      >
                        Партнери (Partners)
                      </label>
                      <Input
                        placeholder="Наприклад: 30+"
                        value={siteSettings.stats_partners || ""}
                        onChange={(e) =>
                          setSiteSettings({
                            ...siteSettings,
                            stats_partners: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#00ffff",
                          fontWeight: "600",
                        }}
                      >
                        Країни (Countries)
                      </label>
                      <Input
                        placeholder="Наприклад: 15+"
                        value={siteSettings.stats_countries || ""}
                        onChange={(e) =>
                          setSiteSettings({
                            ...siteSettings,
                            stats_countries: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* CTA Buttons Section */}
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1px solid #333",
                  }}
                >
                  <h3
                    style={{
                      color: "#00ffff",
                      marginBottom: "15px",
                      fontSize: "18px",
                    }}
                  >
                    Кнопки дії (CTA)
                  </h3>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Кнопка "Стати частиною історії" / "Become Part of History"
                    </label>
                    <Input
                      placeholder="URL для кнопки реєстрації"
                      value={siteSettings.cta_become_part_url || ""}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          cta_become_part_url: e.target.value,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>
                  <div style={{ marginTop: "15px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Кнопка "Дізнатися більше" / "Learn More"
                    </label>
                    <Input
                      placeholder="URL для детальної інформації"
                      value={siteSettings.cta_learn_more_url || ""}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          cta_learn_more_url: e.target.value,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>
                </div>

                {/* Venue Settings */}
                <div
                  style={{
                    marginTop: "30px",
                    padding: "20px",
                    background: "rgba(0, 255, 255, 0.05)",
                    borderRadius: "8px",
                    border: "1px solid rgba(0, 255, 255, 0.2)",
                  }}
                >
                  <h3
                    style={{
                      color: "#00ffff",
                      fontSize: "18px",
                      marginBottom: "16px",
                      fontWeight: "700",
                    }}
                  >
                    📍 Налаштування локації
                  </h3>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        cursor: "pointer",
                        padding: "12px",
                        background: "rgba(0, 0, 0, 0.3)",
                        borderRadius: "6px",
                        border: `2px solid ${
                          siteSettings.venue_revealed ? "#00ff00" : "#666"
                        }`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={siteSettings.venue_revealed || false}
                        onChange={(e) =>
                          setSiteSettings({
                            ...siteSettings,
                            venue_revealed: e.target.checked,
                          })
                        }
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#00ffff",
                        }}
                      />
                      <div>
                        <div
                          style={{
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          {siteSettings.venue_revealed
                            ? "✅ Локація розкрита"
                            : "🔒 Локація закрита"}
                        </div>
                        <div style={{ color: "#999", fontSize: "13px" }}>
                          {siteSettings.venue_revealed
                            ? "Відвідувачі бачать реальне місце проведення з картою"
                            : 'Відвідувачі бачують повідомлення "Буде оголошено за 3 дні"'}
                        </div>
                      </div>
                    </label>
                  </div>

                  {siteSettings.venue_revealed && (
                    <>
                      <div style={{ marginBottom: "15px" }}>
                        <label style={{ color: "#999", fontSize: "14px" }}>
                          Назва локації (UA)
                        </label>
                        <Input
                          value={siteSettings.venue_name || ""}
                          onChange={(e) =>
                            setSiteSettings({
                              ...siteSettings,
                              venue_name: e.target.value,
                            })
                          }
                          placeholder="КВЦ Парковий"
                          style={{
                            background: "#222",
                            border: "1px solid #444",
                            color: "#fff",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <label style={{ color: "#999", fontSize: "14px" }}>
                          Venue Name (EN)
                        </label>
                        <Input
                          value={siteSettings.venue_name_en || ""}
                          onChange={(e) =>
                            setSiteSettings({
                              ...siteSettings,
                              venue_name_en: e.target.value,
                            })
                          }
                          placeholder="Parkovy Convention and Exhibition Center"
                          style={{
                            background: "#222",
                            border: "1px solid #444",
                            color: "#fff",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <label style={{ color: "#999", fontSize: "14px" }}>
                          Адреса (UA)
                        </label>
                        <Input
                          value={siteSettings.venue_address || ""}
                          onChange={(e) =>
                            setSiteSettings({
                              ...siteSettings,
                              venue_address: e.target.value,
                            })
                          }
                          placeholder="Паркова дорога, 16а, 01021, Київ, Україна"
                          style={{
                            background: "#222",
                            border: "1px solid #444",
                            color: "#fff",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <label style={{ color: "#999", fontSize: "14px" }}>
                          Address (EN)
                        </label>
                        <Input
                          value={siteSettings.venue_address_en || ""}
                          onChange={(e) =>
                            setSiteSettings({
                              ...siteSettings,
                              venue_address_en: e.target.value,
                            })
                          }
                          placeholder="Parkova Doroha, 16a, 01021, Kyiv, Ukraine"
                          style={{
                            background: "#222",
                            border: "1px solid #444",
                            color: "#fff",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <label style={{ color: "#999", fontSize: "14px" }}>
                          Посилання на сайт
                        </label>
                        <Input
                          value={siteSettings.venue_url || ""}
                          onChange={(e) =>
                            setSiteSettings({
                              ...siteSettings,
                              venue_url: e.target.value,
                            })
                          }
                          placeholder="https://parkovy.com.ua/"
                          style={{
                            background: "#222",
                            border: "1px solid #444",
                            color: "#fff",
                          }}
                        />
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "15px",
                        }}
                      >
                        <div>
                          <label style={{ color: "#999", fontSize: "14px" }}>
                            Latitude (Широта)
                          </label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={siteSettings.venue_lat || 50.4356}
                            onChange={(e) =>
                              setSiteSettings({
                                ...siteSettings,
                                venue_lat: parseFloat(e.target.value),
                              })
                            }
                            placeholder="50.4356"
                            style={{
                              background: "#222",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ color: "#999", fontSize: "14px" }}>
                            Longitude (Довгота)
                          </label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={siteSettings.venue_lng || 30.5463}
                            onChange={(e) =>
                              setSiteSettings({
                                ...siteSettings,
                                venue_lng: parseFloat(e.target.value),
                              })
                            }
                            placeholder="30.5463"
                            style={{
                              background: "#222",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: "12px",
                          padding: "12px",
                          background: "rgba(0, 255, 0, 0.05)",
                          border: "1px solid rgba(0, 255, 0, 0.2)",
                          borderRadius: "4px",
                        }}
                      >
                        <p
                          style={{
                            color: "#00ff00",
                            fontSize: "12px",
                            marginBottom: "6px",
                            fontWeight: "600",
                          }}
                        >
                          💡 Як отримати координати:
                        </p>
                        <ul
                          style={{
                            color: "#999",
                            fontSize: "11px",
                            margin: 0,
                            paddingLeft: "20px",
                            lineHeight: "1.6",
                          }}
                        >
                          <li>Відкрийте Google Maps</li>
                          <li>Клікніть правою кнопкою на потрібному місці</li>
                          <li>Оберіть перший рядок з координатами</li>
                          <li>Координати скопіюються автоматично</li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                <Button
                  onClick={handleSaveSettings}
                  style={{
                    background: "#00ffff",
                    color: "#000",
                    marginTop: "10px",
                  }}
                >
                  <Save size={18} style={{ marginRight: "6px" }} />
                  Зберегти налаштування
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <AnalyticsDashboard backendUrl={BACKEND_URL} token={token} />
        )}

        {/* Create Button */}
        {activeTab !== "settings" && activeTab !== "analytics" && (
          <Button
            onClick={startCreate}
            style={{
              marginBottom: "20px",
              background: "#00ffff",
              color: "#000",
            }}
          >
            <Plus size={20} style={{ marginRight: "8px" }} />
            Створити новий
          </Button>
        )}

        {/* Form */}
        {showForm && (
          <Card
            style={{
              marginBottom: "30px",
              background: "#1a1a1a",
              border: "1px solid #333",
            }}
          >
            <CardHeader>
              <CardTitle style={{ color: "#fff" }}>
                {editingItem ? "Редагувати" : "Створити"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeTab === "speakers" && (
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Ім'я спікера
                    </label>
                    <Input
                      placeholder="Ім'я спікера"
                      value={formData.name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#ffcc00",
                          fontWeight: "600",
                        }}
                      >
                        🇺🇦 Посада (UA)
                      </label>
                      <Input
                        placeholder="Посада українською"
                        value={formData.title || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#0066cc",
                          fontWeight: "600",
                        }}
                      >
                        🇬🇧 Position (EN)
                      </label>
                      <Input
                        placeholder="Position in English"
                        value={formData.title_en || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, title_en: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#ffcc00",
                          fontWeight: "600",
                        }}
                      >
                        🇺🇦 Компанія (UA)
                      </label>
                      <Input
                        placeholder="Компанія українською"
                        value={formData.company || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, company: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#0066cc",
                          fontWeight: "600",
                        }}
                      >
                        🇬🇧 Company (EN)
                      </label>
                      <Input
                        placeholder="Company in English"
                        value={formData.company_en || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            company_en: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <ImageUploader
                    label="Фото спікера"
                    value={formData.photo || ""}
                    onChange={(url) => setFormData({ ...formData, photo: url })}
                  />

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#ffcc00",
                          fontWeight: "600",
                        }}
                      >
                        🇺🇦 Біографія (UA)
                      </label>
                      <Textarea
                        placeholder="Біографія українською"
                        value={formData.bio || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, bio: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                        rows={4}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#0066cc",
                          fontWeight: "600",
                        }}
                      >
                        🇬🇧 Biography (EN)
                      </label>
                      <Textarea
                        placeholder="Biography in English"
                        value={formData.bio_en || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, bio_en: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                        rows={4}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      border: "1px solid #444",
                      padding: "15px",
                      borderRadius: "8px",
                    }}
                  >
                    <p
                      style={{
                        color: "#00ffff",
                        marginBottom: "10px",
                        fontWeight: "600",
                      }}
                    >
                      Соціальні мережі (опціонально)
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <Input
                        placeholder="Twitter"
                        value={formData.social_links?.twitter || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            social_links: {
                              ...formData.social_links,
                              twitter: e.target.value,
                            },
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                      <Input
                        placeholder="Instagram"
                        value={formData.social_links?.instagram || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            social_links: {
                              ...formData.social_links,
                              instagram: e.target.value,
                            },
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                      <Input
                        placeholder="LinkedIn"
                        value={formData.social_links?.linkedin || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            social_links: {
                              ...formData.social_links,
                              linkedin: e.target.value,
                            },
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                      <Input
                        placeholder="Website"
                        value={formData.social_links?.website || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            social_links: {
                              ...formData.social_links,
                              website: e.target.value,
                            },
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ccc",
                      }}
                    >
                      Порядок відображення
                    </label>
                    <Input
                      type="number"
                      placeholder="Порядок (0-100)"
                      value={formData.order || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        maxWidth: "150px",
                      }}
                    />
                  </div>
                </div>
              )}

              {activeTab === "partners" && (
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Назва партнера
                    </label>
                    <Input
                      placeholder="Назва"
                      value={formData.name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div>
                    <ImageUploader
                      label="Логотип партнера"
                      value={formData.logo || ""}
                      onChange={(url) =>
                        setFormData({ ...formData, logo: url })
                      }
                    />
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "12px",
                        background: "rgba(0, 255, 255, 0.05)",
                        border: "1px solid rgba(0, 255, 255, 0.2)",
                        borderRadius: "4px",
                      }}
                    >
                      <p
                        style={{
                          color: "#00ffff",
                          fontSize: "12px",
                          marginBottom: "6px",
                          fontWeight: "600",
                        }}
                      >
                        💡 Рекомендований формат логотипу:
                      </p>
                      <ul
                        style={{
                          color: "#999",
                          fontSize: "11px",
                          margin: 0,
                          paddingLeft: "20px",
                          lineHeight: "1.6",
                        }}
                      >
                        <li>Формат: PNG з прозорим фоном або SVG</li>
                        <li>
                          <strong>
                            Оновлено: 400px × 160px (горизонтальні) або 280px ×
                            280px (квадратні)
                          </strong>
                        </li>
                        <li>Світлий логотип на прозорому фоні</li>
                        <li>Файл до 200KB</li>
                        <li>
                          Логотип буде відображатися по центру в контейнері
                          280×120px
                        </li>
                        <li>
                          Статус (Gold/Silver/Platinum) буде у правому верхньому
                          кутку
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Сайт партнера
                    </label>
                    <Input
                      placeholder="https://example.com"
                      value={formData.website_url || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          website_url: e.target.value,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Рівень партнерства
                    </label>
                    <select
                      value={formData.tier || "none"}
                      onChange={(e) =>
                        setFormData({ ...formData, tier: e.target.value })
                      }
                      style={{
                        padding: "10px",
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    >
                      <option value="platinum">💎 Platinum</option>
                      <option value="gold">🥇 Gold</option>
                      <option value="silver">🥈 Silver</option>
                      <option value="none">
                        🤝 Без рівня (звичайний партнер)
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ccc",
                      }}
                    >
                      Порядок відображення
                    </label>
                    <Input
                      type="number"
                      placeholder="Порядок (0-100)"
                      value={formData.order || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        maxWidth: "150px",
                      }}
                    />
                  </div>
                </div>
              )}

              {activeTab === "mediaPartners" && (
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Назва медіа-партнера
                    </label>
                    <Input
                      placeholder="Назва"
                      value={formData.name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div>
                    <ImageUploader
                      label="Логотип медіа-партнера"
                      value={formData.logo || ""}
                      onChange={(url) =>
                        setFormData({ ...formData, logo: url })
                      }
                    />
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "12px",
                        background: "rgba(0, 255, 255, 0.05)",
                        border: "1px solid rgba(0, 255, 255, 0.2)",
                        borderRadius: "4px",
                      }}
                    >
                      <p
                        style={{
                          color: "#00ffff",
                          fontSize: "12px",
                          marginBottom: "6px",
                          fontWeight: "600",
                        }}
                      >
                        💡 Рекомендований формат логотипу:
                      </p>
                      <ul
                        style={{
                          color: "#999",
                          fontSize: "11px",
                          margin: 0,
                          paddingLeft: "20px",
                          lineHeight: "1.6",
                        }}
                      >
                        <li>Формат: PNG з прозорим фоном або SVG</li>
                        <li>
                          <strong>
                            Оновлено: 400px × 160px (горизонтальні) або 280px ×
                            280px (квадратні)
                          </strong>
                        </li>
                        <li>Світлий логотип на прозорому фоні</li>
                        <li>Файл до 200KB</li>
                        <li>
                          Логотип буде відображатися по центру в контейнері
                          280×120px
                        </li>
                        <li>
                          Статус (Gold/Silver/Platinum) буде у правому верхньому
                          кутку
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Сайт медіа-партнера
                    </label>
                    <Input
                      placeholder="https://example.com"
                      value={formData.website_url || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          website_url: e.target.value,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ccc",
                      }}
                    >
                      Порядок відображення
                    </label>
                    <Input
                      type="number"
                      placeholder="Порядок (0-100)"
                      value={formData.order || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        maxWidth: "150px",
                      }}
                    />
                  </div>
                </div>
              )}

              {activeTab === "schedule" && (
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Час
                    </label>
                    <Input
                      placeholder="09:00 - 10:00"
                      value={formData.time || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, time: e.target.value })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#ffcc00",
                          fontWeight: "600",
                        }}
                      >
                        🇺🇦 Назва (UA)
                      </label>
                      <Input
                        placeholder="Назва українською"
                        value={formData.title || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#0066cc",
                          fontWeight: "600",
                        }}
                      >
                        🇬🇧 Title (EN)
                      </label>
                      <Input
                        placeholder="Title in English"
                        value={formData.title_en || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, title_en: e.target.value })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ccc",
                      }}
                    >
                      Спікер (опціонально)
                    </label>
                    <Input
                      placeholder="Ім'я спікера"
                      value={formData.speaker || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, speaker: e.target.value })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ff00ff",
                        fontWeight: "600",
                      }}
                    >
                      👥 Учасники панельної дискусії (опціонально)
                    </label>
                    <textarea
                      placeholder="Введіть імена учасників панелі, кожне з нового рядка:&#10;Джон Доу&#10;Джейн Сміт&#10;Алекс Браун"
                      value={formData.speakers?.join("\n") || ""}
                      onChange={(e) => {
                        const speakers = e.target.value
                          .split("\n")
                          .filter((s) => s.trim());
                        setFormData({
                          ...formData,
                          speakers: speakers.length > 0 ? speakers : null,
                        });
                      }}
                      rows="5"
                      style={{
                        padding: "10px",
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        borderRadius: "4px",
                        width: "100%",
                        fontFamily: "inherit",
                        resize: "vertical",
                      }}
                    />
                    <p
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        marginTop: "6px",
                      }}
                    >
                      💡 Для панельних дискусій: кожен учасник з нового рядка.
                      Якщо вказані учасники панелі, поле "Спікер" ігнорується.
                    </p>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#00ffff",
                        fontWeight: "600",
                      }}
                    >
                      Тип події
                    </label>
                    <select
                      value={formData.type || "session"}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                    >
                      <option value="registration">
                        📝 Реєстрація (Registration)
                      </option>
                      <option value="opening">🎬 Вступ (Opening)</option>
                      <option value="speech">🎤 Виступ (Speech)</option>
                      <option value="presentation">
                        📊 Презентація (Presentation)
                      </option>
                      <option value="session">📋 Сесія (Session)</option>
                      <option value="qa">
                        ❓ Сесія питань та відповідей (Q&A)
                      </option>
                      <option value="keynote">⭐ Доповідь (Keynote)</option>
                      <option value="panel">👥 Панель (Panel)</option>
                      <option value="workshop">🛠️ Воркшоп (Workshop)</option>
                      <option value="break">☕ Перерва (Break)</option>
                      <option value="networking">
                        🤝 Нетворкінг (Networking)
                      </option>
                    </select>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "15px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#ffcc00",
                          fontWeight: "600",
                        }}
                      >
                        🇺🇦 Опис (UA)
                      </label>
                      <Textarea
                        placeholder="Опис українською"
                        value={formData.description || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                        rows={3}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          color: "#0066cc",
                          fontWeight: "600",
                        }}
                      >
                        🇬🇧 Description (EN)
                      </label>
                      <Textarea
                        placeholder="Description in English"
                        value={formData.description_en || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description_en: e.target.value,
                          })
                        }
                        style={{
                          background: "#222",
                          border: "1px solid #444",
                          color: "#fff",
                        }}
                        rows={3}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#ccc",
                      }}
                    >
                      Порядок відображення
                    </label>
                    <Input
                      type="number"
                      placeholder="Порядок (0-100)"
                      value={formData.order || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order: parseInt(e.target.value) || 0,
                        })
                      }
                      style={{
                        background: "#222",
                        border: "1px solid #444",
                        color: "#fff",
                        maxWidth: "150px",
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <Button
                  onClick={editingItem ? handleUpdate : handleCreate}
                  style={{ background: "#00ffff", color: "#000" }}
                >
                  <Save size={18} style={{ marginRight: "6px" }} />
                  {editingItem ? "Оновити" : "Створити"}
                </Button>
                <Button
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    setFormData({});
                  }}
                  style={{ background: "#666", color: "#fff" }}
                >
                  <X size={18} style={{ marginRight: "6px" }} />
                  Скасувати
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data List */}
        <div style={{ display: "grid", gap: "15px" }}>
          {activeTab === "speakers" && speakers.length === 0 && (
            <Card
              style={{
                background: "#1a1a1a",
                border: "1px dashed #444",
                padding: "40px",
                textAlign: "center",
              }}
            >
              <p style={{ color: "#666" }}>
                Спікерів поки немає. Натисніть "Створити новий" щоб додати.
              </p>
            </Card>
          )}

          {activeTab === "speakers" &&
            speakers.map((speaker) => (
              <Card
                key={speaker.id}
                style={{ background: "#1a1a1a", border: "1px solid #333" }}
              >
                <CardContent
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    {speaker.photo && (
                      <img
                        src={speaker.photo}
                        alt={speaker.name}
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <div>
                      <h3 style={{ color: "#00ffff", marginBottom: "5px" }}>
                        {speaker.name}
                      </h3>
                      <p style={{ color: "#ccc", fontSize: "14px" }}>
                        {speaker.title}{" "}
                        {speaker.title_en && (
                          <span style={{ color: "#666" }}>
                            / {speaker.title_en}
                          </span>
                        )}
                      </p>
                      <p style={{ color: "#888", fontSize: "12px" }}>
                        {speaker.company}{" "}
                        {speaker.company_en && (
                          <span>/ {speaker.company_en}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => startEdit(speaker)}
                      style={{
                        padding: "8px 12px",
                        background: "#444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(speaker.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#d32f2f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}

          {activeTab === "partners" && partners.length === 0 && (
            <Card
              style={{
                background: "#1a1a1a",
                border: "1px dashed #444",
                padding: "40px",
                textAlign: "center",
              }}
            >
              <p style={{ color: "#666" }}>
                Партнерів поки немає. Натисніть "Створити новий" щоб додати.
              </p>
            </Card>
          )}

          {activeTab === "partners" &&
            partners.map((partner) => (
              <Card
                key={partner.id}
                style={{ background: "#1a1a1a", border: "1px solid #333" }}
              >
                <CardContent
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    {partner.logo && (
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        style={{
                          width: "80px",
                          height: "auto",
                          maxHeight: "50px",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <div>
                      <h3 style={{ color: "#00ffff", marginBottom: "5px" }}>
                        {partner.name}
                      </h3>
                      {partner.tier !== "none" && (
                        <p
                          style={{
                            color: "#ccc",
                            fontSize: "14px",
                            textTransform: "uppercase",
                          }}
                        >
                          {partner.tier === "platinum" && "💎"}{" "}
                          {partner.tier === "gold" && "🥇"}{" "}
                          {partner.tier === "silver" && "🥈"} {partner.tier}
                        </p>
                      )}
                      {partner.tier === "none" && (
                        <p style={{ color: "#666", fontSize: "14px" }}>
                          🤝 Партнер
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => startEdit(partner)}
                      style={{
                        padding: "8px 12px",
                        background: "#444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(partner.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#d32f2f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}

          {activeTab === "mediaPartners" && mediaPartners.length === 0 && (
            <Card
              style={{
                background: "#1a1a1a",
                border: "1px dashed #444",
                padding: "40px",
                textAlign: "center",
              }}
            >
              <p style={{ color: "#666" }}>
                Медіа-партнерів поки немає. Натисніть "Створити новий" щоб
                додати.
              </p>
            </Card>
          )}

          {activeTab === "mediaPartners" &&
            mediaPartners.map((mediaPartner) => (
              <Card
                key={mediaPartner.id}
                style={{ background: "#1a1a1a", border: "1px solid #333" }}
              >
                <CardContent
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "15px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    {mediaPartner.logo && (
                      <img
                        src={mediaPartner.logo}
                        alt={mediaPartner.name}
                        style={{
                          width: "80px",
                          height: "auto",
                          maxHeight: "50px",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <div>
                      <h3 style={{ color: "#00ffff", marginBottom: "5px" }}>
                        {mediaPartner.name}
                      </h3>
                      {mediaPartner.website_url && (
                        <p style={{ color: "#ccc", fontSize: "12px" }}>
                          <a
                            href={mediaPartner.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#666" }}
                          >
                            {mediaPartner.website_url}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => startEdit(mediaPartner)}
                      style={{
                        padding: "8px 12px",
                        background: "#444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(mediaPartner.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#d32f2f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}

          {activeTab === "schedule" && schedule.length === 0 && (
            <Card
              style={{
                background: "#1a1a1a",
                border: "1px dashed #444",
                padding: "40px",
                textAlign: "center",
              }}
            >
              <p style={{ color: "#666" }}>
                Програма порожня. Натисніть "Створити новий" щоб додати пункт.
              </p>
            </Card>
          )}

          {activeTab === "schedule" &&
            schedule.map((item) => (
              <Card
                key={item.id}
                style={{ background: "#1a1a1a", border: "1px solid #333" }}
              >
                <CardContent
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "15px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#00ffff",
                        marginBottom: "5px",
                        fontWeight: "600",
                      }}
                    >
                      {item.time}
                    </div>
                    <h3 style={{ color: "#fff", marginBottom: "5px" }}>
                      {item.title}{" "}
                      {item.title_en && (
                        <span style={{ color: "#666", fontWeight: "normal" }}>
                          / {item.title_en}
                        </span>
                      )}
                    </h3>
                    {item.speakers && item.speakers.length > 0 ? (
                      <div style={{ marginTop: "8px" }}>
                        <p
                          style={{
                            color: "#ff00ff",
                            fontSize: "14px",
                            fontWeight: "600",
                            marginBottom: "4px",
                          }}
                        >
                          👥 Учасники панелі:
                        </p>
                        <ul
                          style={{
                            color: "#ccc",
                            fontSize: "14px",
                            marginLeft: "20px",
                            marginTop: "4px",
                          }}
                        >
                          {item.speakers.map((speaker, idx) => (
                            <li key={idx}>{speaker}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      item.speaker && (
                        <p style={{ color: "#ccc", fontSize: "14px" }}>
                          Спікер: {item.speaker}
                        </p>
                      )
                    )}
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: "8px",
                        padding: "4px 8px",
                        background: "#333",
                        color: "#00ffff",
                        borderRadius: "4px",
                        fontSize: "12px",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.type}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => startEdit(item)}
                      style={{
                        padding: "8px 12px",
                        background: "#444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#d32f2f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
